import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { BasicButton } from '../../../../../../widgets/buttons';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { TextArea } from '../../../../../../widgets/textArea';
import { Checkbox } from '../../../../../../widgets/checkbox';
import { Tab, TabList, TabPanel } from '../../../../../../widgets/ztabs';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import { AdvancedSelect } from '../../../../../../widgets/select';
import { useProductConfigurationState } from 'src/components/pages/productConfiguration/state';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { T } from 'src/shared/helpers';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import PlayIcon from 'src/shared/material_icons/av/play_arrow.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import OptionIcon from 'src/shared/material_icons/image/palette.svg';
import styled from 'styled-components';
import { COMPOSER_ACTION_COLUMNS, ComposerActionTreeElement } from '../../../configurator-composer/composer/components/composerActionTreeElement';
import Composer3DPreviewCapture from '../../../configurator-composer/composer/components/composer3DPreviewCapture';
import ComposerUploadComponent from '../../../configurator-composer/composer/components/composerUploadComponent';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { ComposerValidationErrorCode, hasComposerValidationError } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';
import ComposerPlatformProductDialog from '../../../configurator-composer/composer/dialogs/composerPlatformProductDialog';
import ComposerSaveActionDialog from '../../../configurator-composer/composer/dialogs/save/composerSaveActionDialog';
import { setActionSceneElementType, sortByDisplay } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { createComposerLocalCode, isComposerStoreCode } from '../../../configurator-composer/composer/helpers/composerLocalCode';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import {
    ComposerPlatformProduct,
    ComposerPlatformVariant,
    createComposerPlatformVariantCode,
    loadComposerPlatformVariants,
    parseComposerPlatformVariantCode
} from '../../../configurator-composer/composer/helpers/composerPlatformPricing';
import { applyComposerOptionImage, createComposerOptionImageReference } from '../../../configurator-composer/composer/helpers/composerOptionImage';
import type { TreeNodeHook } from '../../tree';
import {
    ComposerDetailCard,
    ComposerDetailLabel,
    ComposerDetailsStack,
    ComposerDetailValue,
    ComposerAddButton,
    ComposerEmptyState,
    ComposerFormGroup,
    ComposerFormSection,
    ComposerFormSectionTitle,
    ComposerFormStack,
    ComposerIconButton,
    ComposerKindBadge,
    ComposerList,
    ComposerPanel,
    ComposerScrollBody,
    ComposerSettingsBody,
    ComposerSubjectContent,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectMeta,
    ComposerSubjectNameInput,
    ComposerTabCount,
    ComposerTabs,
    ComposerToolbar,
    ComposerToolbarSpacer,
    ComposerUnsupportedNote
} from '../composerWorkspace';
import ComposerLinksEditor from './composerLinksEditor';
import {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerEcommercePlatform,
    ComposerExtensionFieldNode,
    ComposerOptionActionNode,
    ComposerPricingNode
} from './composerAttributesModels';

const TabBody = styled.div`
    min-height: 0;
    flex: 1;
    overflow: auto;
    overscroll-behavior: contain;
    padding: 8px;
`;

const ActionTableHeader = styled.div`
    display: grid;
    min-width: 0;
    min-height: 26px;
    grid-template-columns: ${COMPOSER_ACTION_COLUMNS};
    align-items: center;
    gap: 6px;
    /* Scroll body padding plus row padding, so the labels line up with the cells below. */
    padding: 0 12px 0 14px;
    /* Same lettering as the Links table header: small, bold and upper case. */
    color: var(--composer-faint, #78909c);
    background: var(--composer-sunken, #f9f9f9);
    border-bottom: 1px solid var(--composer-line-soft, #eef1f3);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const PropertyEditorActions = styled.div`
    display: flex;
    gap: 8px;
`;

const PriceModeOptions = styled.div`
    display: grid;
    gap: 6px;
`;

const PriceModeLabel = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 9px 10px;
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: 7px;
    cursor: pointer;

    input {
        margin-top: 2px;
        accent-color: var(--composer-accent, #e8650a);
    }

    span {
        display: grid;
        gap: 2px;
    }

    small {
        color: var(--composer-muted, #5c6b7a);
        font-size: 11px;
    }
`;

const ProductSelectorCard = styled.div`
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr) auto;
    align-items: center;
    gap: 9px;
    padding: 8px;
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: 7px;

    > div:nth-child(2) {
        min-width: 0;
        display: grid;
        gap: 2px;
    }

    strong,
    small {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    small {
        color: var(--composer-muted, #5c6b7a);
        font-size: 10px;
    }
`;

const ProductThumbnail = styled.div`
    width: 42px;
    height: 42px;
    display: grid;
    place-items: center;
    overflow: hidden;
    border-radius: 5px;
    background: var(--composer-sunken, #f4f7fa);
    color: var(--composer-muted, #5c6b7a);

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    svg {
        width: 20px;
        height: 20px;
        fill: currentColor;
    }
`;

const ComposerWarningNote = styled.div`
    padding: 7px 9px;
    border: 1px solid var(--composer-warn-line-strong, #f2c98d);
    border-radius: 6px;
    color: var(--composer-warn-ink, #874b0b);
    background: var(--composer-warn-surface, #fff8ed);
    font-size: 11px;
    line-height: 1.35;
`;

const PropertiesContent: FC<{ selectedOption: ComposerAttributeOptionNode; onUpdate: (option: ComposerAttributeOptionNode) => void }> = ({
    selectedOption,
    onUpdate
}) => {
    const globalFields = useComposerStore((store) => store.extensionFieldDefinitions);
    const [isEditing, setIsEditing] = React.useState(false);
    const [editValues, setEditValues] = React.useState<Record<string, string>>({});
    const fields = selectedOption.extensionFields || [];

    const startEditing = () => {
        setEditValues(Object.fromEntries(fields.map((field) => [String(field.extensionFieldID), field.value])));
        setIsEditing(true);
    };

    const save = () => {
        const extensionFields: ComposerExtensionFieldNode[] = globalFields
            .filter((field) => editValues[String(field.fieldID)] !== undefined && editValues[String(field.fieldID)] !== '')
            .map((field) => ({
                extensionFieldID: Number(field.fieldID),
                name: field.name || '',
                label: field.label || '',
                value: editValues[String(field.fieldID)] || '',
                dataTypeID: field.dataTypeID,
                operationTypeID: field.operationTypeID,
                formatString: field.formatString || ''
            }));
        onUpdate({ ...selectedOption, extensionFields });
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <ComposerDetailsStack>
                {globalFields.length === 0 ? (
                    <ComposerUnsupportedNote>{T._('No extension fields are configured in Additional Properties.', 'Admin')}</ComposerUnsupportedNote>
                ) : (
                    globalFields.map((field) => (
                        <FormControl key={String(field.fieldID)} label={field.label || field.name || ''}>
                            <Input
                                type='text'
                                placeholder={field.dataTypeName || ''}
                                value={editValues[String(field.fieldID)] || ''}
                                onChange={(event) => setEditValues((current) => ({ ...current, [String(field.fieldID)]: event.target.value }))}
                            />
                        </FormControl>
                    ))
                )}
                <PropertyEditorActions>
                    <BasicButton onClick={save}>{T._('Save', 'Admin')}</BasicButton>
                    <BasicButton onClick={() => setIsEditing(false)}>{T._('Cancel', 'Admin')}</BasicButton>
                </PropertyEditorActions>
            </ComposerDetailsStack>
        );
    }

    return (
        <ComposerDetailsStack>
            {fields.length === 0 ? (
                <ComposerEmptyState>{T._('No additional properties are set for this option.', 'Admin')}</ComposerEmptyState>
            ) : (
                fields.map((field) => (
                    <ComposerDetailCard key={field.extensionFieldID}>
                        <ComposerDetailLabel>{field.label || field.name}</ComposerDetailLabel>
                        <ComposerDetailValue>{field.value}</ComposerDetailValue>
                    </ComposerDetailCard>
                ))
            )}
            <BasicButton onClick={startEditing} disabled={globalFields.length === 0}>
                {fields.length === 0 ? T._('Add properties', 'Admin') : T._('Edit properties', 'Admin')}
            </BasicButton>
        </ComposerDetailsStack>
    );
};

const PriceContent: FC<{
    selectedAttribute: ComposerAttributeNode;
    selectedOption: ComposerAttributeOptionNode;
    ecommercePlatform: ComposerEcommercePlatform;
    onUpdateAttribute: (attribute: ComposerAttributeNode) => void;
    onUpdateOption: (option: ComposerAttributeOptionNode) => void;
}> = ({ selectedAttribute, selectedOption, ecommercePlatform, onUpdateAttribute, onUpdateOption }) => {
    const { state: productState } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const optionPricing = (selectedAttribute.pricings || []).find((pricing) => pricing.selectedOptionGuid === selectedOption.id);
    const [price, setPrice] = React.useState(optionPricing?.price?.toString() || '');
    const [chargeName, setChargeName] = React.useState(optionPricing?.name || selectedOption.name);
    const linkedVariant = React.useMemo(() => parseComposerPlatformVariantCode(selectedOption.code), [selectedOption.code]);
    const isPlatformCharge = ecommercePlatform === ComposerEcommercePlatform.Shopify || ecommercePlatform === ComposerEcommercePlatform.BigCommerce;
    const [adjustPrice, setAdjustPrice] = React.useState(Boolean(optionPricing || linkedVariant));
    const [priceMode, setPriceMode] = React.useState<'charge' | 'variant'>(linkedVariant ? 'variant' : 'charge');
    const [selectedProduct, setSelectedProduct] = React.useState<ComposerPlatformProduct | null>(
        linkedVariant
            ? {
                  code: linkedVariant.product.code,
                  name: linkedVariant.product.code,
                  metadata: linkedVariant.product.metadata
              }
            : null
    );
    const [variants, setVariants] = React.useState<ComposerPlatformVariant[]>(linkedVariant ? [linkedVariant.variant] : []);
    const [variantsLoading, setVariantsLoading] = React.useState(false);
    const [variantsError, setVariantsError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const pricing = (selectedAttribute.pricings || []).find((candidate) => candidate.selectedOptionGuid === selectedOption.id);
        setPrice(pricing?.price?.toString() || '');
        setChargeName(pricing?.name || selectedOption.name);
        const currentVariant = parseComposerPlatformVariantCode(selectedOption.code);
        setAdjustPrice(Boolean(pricing || currentVariant));
        setPriceMode(currentVariant ? 'variant' : 'charge');
        setSelectedProduct((currentProduct) =>
            currentVariant
                ? currentProduct?.code === currentVariant.product.code
                    ? currentProduct
                    : { code: currentVariant.product.code, name: currentVariant.product.code, metadata: currentVariant.product.metadata }
                : null
        );
        setVariants((currentVariants) =>
            currentVariant ? (currentVariants.some((variant) => variant.code === currentVariant.variant.code) ? currentVariants : [currentVariant.variant]) : []
        );
        setVariantsError(null);
        // A mode change clears the old price before a variant is selected. Syncing on
        // every intermediate store update would consequently turn Adjust price off.
        // The selected option is the boundary at which this local price UI must reset.
    }, [selectedOption.id]);

    const removeOptionPricing = () => {
        onUpdateAttribute({
            ...selectedAttribute,
            pricings: (selectedAttribute.pricings || []).filter((pricing) => pricing.selectedOptionGuid !== selectedOption.id)
        });
    };

    const upsertOptionPricing = (value: string, name = chargeName) => {
        const currentPricings = [...(selectedAttribute.pricings || [])];
        const index = currentPricings.findIndex((pricing) => pricing.selectedOptionGuid === selectedOption.id);
        const pricing: ComposerPricingNode = {
            attributePricingID: optionPricing?.attributePricingID || 0,
            selectedOptionID: selectedOption.idNumeric || 0,
            selectedOptionGuid: selectedOption.id,
            selectedValue: '',
            price: Number.parseFloat(value) || 0,
            pricingID: optionPricing?.pricingID || 0,
            name: name.trim() || selectedOption.name
        };
        index >= 0 ? (currentPricings[index] = pricing) : currentPricings.push(pricing);
        onUpdateAttribute({ ...selectedAttribute, pricings: currentPricings });
    };

    const updatePrice = (value: string) => {
        setPrice(value);
        upsertOptionPricing(value);
    };

    const selectProduct = async (product: ComposerPlatformProduct) => {
        setSelectedProduct(product);
        setVariants([]);
        setVariantsLoading(true);
        setVariantsError(null);
        try {
            const loadedVariants = await loadComposerPlatformVariants(product.code);
            setVariants(loadedVariants);
            if (loadedVariants.length === 1) {
                onUpdateOption({
                    ...selectedOption,
                    platformAttribute: undefined,
                    code: createComposerPlatformVariantCode(product, loadedVariants[0])
                });
            } else {
                onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
            }
        } catch (error) {
            setVariantsError(T._('Store variants could not be loaded. Choose the product again or retry later.', 'Admin'));
        } finally {
            setVariantsLoading(false);
        }
    };

    const setPlatformPricingEnabled = (enabled: boolean) => {
        setAdjustPrice(enabled);
        setVariantsError(null);
        if (!enabled) {
            removeOptionPricing();
            setSelectedProduct(null);
            setVariants([]);
            onUpdateOption({
                ...selectedOption,
                platformAttribute: undefined,
                code: selectedOption.code.trim() && !linkedVariant ? selectedOption.code : createComposerLocalCode(selectedOption.name, 'option')
            });
            return;
        }

        setPriceMode('charge');
        setPrice(optionPricing?.price?.toString() || '');
        setChargeName(optionPricing?.name || selectedOption.name);
        upsertOptionPricing(optionPricing?.price?.toString() || '', optionPricing?.name || selectedOption.name);
        onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
    };

    const setPlatformPriceMode = (mode: 'charge' | 'variant') => {
        setPriceMode(mode);
        setVariantsError(null);
        if (mode === 'charge') {
            setSelectedProduct(null);
            setVariants([]);
            upsertOptionPricing(price, chargeName);
            onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
        } else {
            removeOptionPricing();
            onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
        }
    };

    if (isPlatformCharge) {
        return (
            <ComposerDetailsStack>
                <Checkbox label={T._('Adjust price', 'Admin')} checked={adjustPrice} onChange={setPlatformPricingEnabled} />
                {adjustPrice && (
                    <>
                        <PriceModeOptions role='radiogroup' aria-label={T._('Price adjustment type', 'Admin')}>
                            <PriceModeLabel>
                                <input type='radio' checked={priceMode === 'charge'} onChange={() => setPlatformPriceMode('charge')} />
                                <span>
                                    <strong>{T._('Create a charge', 'Admin')}</strong>
                                    <small>{T._('Creates an active product that remains hidden in your store.', 'Admin')}</small>
                                </span>
                            </PriceModeLabel>
                            <PriceModeLabel>
                                <input type='radio' checked={priceMode === 'variant'} onChange={() => setPlatformPriceMode('variant')} />
                                <span>
                                    <strong>{T._('Use a variant from another (existing) product in your shop', 'Admin')}</strong>
                                    <small>{T._('Use the price of a variant that already exists in the store.', 'Admin')}</small>
                                </span>
                            </PriceModeLabel>
                        </PriceModeOptions>
                        {priceMode === 'charge' ? (
                            <>
                                <FormControl label={T._('Charge name', 'Admin')}>
                                    <Input
                                        value={chargeName}
                                        placeholder={selectedOption.name}
                                        onChange={(event) => {
                                            setChargeName(event.target.value);
                                            upsertOptionPricing(price, event.target.value);
                                        }}
                                    />
                                </FormControl>
                                <FormControl label={T._('Price', 'Admin')}>
                                    <Input type='number' placeholder='0.00' value={price} onChange={(event) => updatePrice(event.target.value)} />
                                </FormControl>
                            </>
                        ) : (
                            <>
                                <ProductSelectorCard>
                                    <ProductThumbnail>
                                        {selectedProduct?.thumbnail ? <img src={selectedProduct.thumbnail} alt='' /> : <OptionIcon />}
                                    </ProductThumbnail>
                                    <div>
                                        <strong>{selectedProduct?.name || T._('No product selected', 'Admin')}</strong>
                                        {selectedProduct?.code && <small>{selectedProduct.code}</small>}
                                    </div>
                                    <BasicButton
                                        onClick={() =>
                                            showDialog(
                                                'composerPlatformProduct',
                                                <ComposerPlatformProductDialog
                                                    salesChannelID={productState.salesChannelID}
                                                    forbiddenProductCode={productState.product.code}
                                                    onClose={() => closeDialog('composerPlatformProduct')}
                                                    onSelect={(product) => void selectProduct(product)}
                                                />
                                            )
                                        }
                                    >
                                        {selectedProduct ? T._('Change product', 'Admin') : T._('Select product', 'Admin')}
                                    </BasicButton>
                                </ProductSelectorCard>
                                {selectedProduct && (
                                    <FormControl label={T._('Store variant', 'Admin')}>
                                        <AdvancedSelect
                                            isClearable
                                            isDisabled={variantsLoading || variants.length === 0}
                                            isLoading={variantsLoading}
                                            options={variants.map((variant) => ({
                                                value: variant.code,
                                                label: variant.name || variant.sku || variant.code,
                                                variant
                                            }))}
                                            value={
                                                linkedVariant
                                                    ? {
                                                          value: linkedVariant.variant.code,
                                                          label: linkedVariant.variant.name || linkedVariant.variant.sku || linkedVariant.variant.code,
                                                          variant: linkedVariant.variant
                                                      }
                                                    : null
                                            }
                                            onChange={(selection) =>
                                                onUpdateOption({
                                                    ...selectedOption,
                                                    platformAttribute: undefined,
                                                    code: selection?.variant ? createComposerPlatformVariantCode(selectedProduct, selection.variant) : ''
                                                })
                                            }
                                            placeholder={variantsLoading ? T._('Loading variants…', 'Admin') : T._('Select a variant', 'Admin')}
                                            menuPosition='fixed'
                                        />
                                    </FormControl>
                                )}
                                {variantsError && <ComposerWarningNote>{variantsError}</ComposerWarningNote>}
                            </>
                        )}
                    </>
                )}
            </ComposerDetailsStack>
        );
    }

    return (
        <ComposerDetailsStack>
            <FormControl label={T._('Option price', 'Admin')} tooltipLabelHtml={T._('Set the price adjustment for this option.', 'Admin')}>
                <Input type='number' placeholder='0.00' value={price} onChange={(event) => updatePrice(event.target.value)} />
            </FormControl>
            <ComposerUnsupportedNote>
                {T._('The current product API stores one price adjustment per option, not a list of prices.', 'Admin')}
            </ComposerUnsupportedNote>
        </ComposerDetailsStack>
    );
};

const ComposerActions: FC<{
    selectedAttribute: ComposerAttributeNode;
    selectedOption?: ComposerAttributeOptionNode;
    actions: ComposerOptionActionNode[];
    multiple: boolean;
    state: TreeNodeHook<ComposerOptionActionNode>;
    materials?: BABYLON.Material[];
    meshes?: BABYLON.AbstractMesh[];
    cameras?: BABYLON.Camera[];
}> = ({ selectedAttribute, selectedOption, actions, multiple, state, materials = [], meshes = [], cameras = [] }) => {
    const { state: productState } = useProductConfigurationState();
    const [sceneEditor] = usePreview3DStore((store) => [store.sceneEditor]);
    const [
        attributes,
        updateAction,
        removeAction,
        updateOption,
        updateAttribute,
        selectActions,
        ecommerceSettings,
        optionFace,
        setOptionFace,
        validateOptions,
        merchantProductOptions,
        printTypes
    ] = useComposerStore((store) => [
        store.attributes,
        store.updateAction,
        store.removeAction,
        store.updateOption,
        store.updateAttribute,
        store.selectActions,
        store.ecommerceSettings,
        store.optionFace,
        store.setOptionFace,
        store.validateOptions,
        store.merchantProductOptions,
        store.printTypes
    ]);
    const { showDialog, closeDialog } = useDialogManager();
    const { selectedIds, filteredData, addNode, updateNode, findNodeById, removeNodes, setSelectedIds } = state;

    React.useEffect(() => {
        setSelectedIds([]);
        selectActions(null);
    }, [selectedOption?.id]);

    const addAction = () => {
        if (!selectedOption) return;
        const action = ComposerAttributeNodeAdapter.toNodeAction(selectedAttribute.id, selectedOption.id, ComposerAttributeNodeAdapter.createNewAction());
        const updatedAction = {
            ...action,
            attributeID: selectedAttribute.id,
            optionID: selectedOption.id,
            displayOrder: actions.length,
            isNew: true
        };
        addNode(updatedAction);
        updateAction(updatedAction);
        setSelectedIds([updatedAction.id]);
        selectActions([updatedAction]);
        setOptionFace('actions');
    };

    const playAction = (action: ComposerOptionActionNode) => {
        sceneEditor?.executeActionsAsync(selectedAttribute.idNumeric, null, [ComposerAttributeNodeAdapter.fromActionNode(action)]);
    };

    const removeActions = (actionsToRemove: ComposerOptionActionNode[]) => {
        if (actionsToRemove.length === 0) return;
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={T._('Are you sure you want to remove the selected action(s)?', 'Admin')}
                buttons={[
                    { label: T._('Cancel', 'Admin'), outline: true, onClick: () => closeDialog('confirmDialog') },
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            const ids = actionsToRemove.map((action) => action.id);
                            removeNodes(ids);
                            actionsToRemove.forEach((action) => removeAction(action.attributeID, action.optionID, action.id));
                            setSelectedIds([]);
                            selectActions(null);
                        }
                    }
                ]}
            />
        );
    };

    const selectAction = (event: React.MouseEvent, action: ComposerOptionActionNode) => {
        setOptionFace('actions');
        if (event.shiftKey && selectedIds.length > 0) {
            const visibleActions = [...filteredData].sort((left, right) => sortByDisplay(filteredData, left, right));
            const start = visibleActions.findIndex((candidate) => candidate.id === selectedIds[selectedIds.length - 1]);
            const end = visibleActions.findIndex((candidate) => candidate.id === action.id);
            if (start < 0 || end < 0) return;
            const range = visibleActions.slice(Math.min(start, end), Math.max(start, end) + 1);
            setSelectedIds(range.map((candidate) => candidate.id));
            selectActions(range);
            return;
        }
        if (event.ctrlKey || event.metaKey) {
            const nextIds = selectedIds.includes(action.id) ? selectedIds.filter((id) => id !== action.id) : [...selectedIds, action.id];
            setSelectedIds(nextIds);
            selectActions(nextIds.map((id) => findNodeById(id)).filter(Boolean) as ComposerOptionActionNode[]);
            return;
        }
        if (selectedIds.length === 1 && selectedIds[0] === action.id) {
            setSelectedIds([]);
            selectActions(null);
            return;
        }
        setSelectedIds([action.id]);
        selectActions([action]);
    };

    const saveInlineAction = (currentAction: ComposerOptionActionNode, savedAction: ComposerOptionActionNode | null) => {
        if (!savedAction || !selectedOption) return;
        const nextAction = setActionSceneElementType({
            ...savedAction,
            id: currentAction.id,
            attributeID: selectedAttribute.id,
            optionID: selectedOption.id,
            displayOrder: currentAction.displayOrder,
            isNew: false
        });
        updateNode(currentAction.id, nextAction);
        updateAction(nextAction);
        selectActions([nextAction]);
    };

    const cancelInlineAction = (action: ComposerOptionActionNode) => {
        if (action.isNew) {
            removeNodes([action.id]);
            removeAction(action.attributeID, action.optionID, action.id);
        }
        setSelectedIds([]);
        selectActions(null);
    };

    const moveAction = (action: ComposerOptionActionNode, direction: 'up' | 'down') => {
        const siblings = [...actions].sort((left, right) => sortByDisplay(actions, left, right));
        const index = siblings.findIndex((candidate) => candidate.id === action.id);
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= siblings.length) return;
        const swap = siblings[swapIndex];
        updateNode(action.id, { ...action, displayOrder: swap.displayOrder });
        updateNode(swap.id, { ...swap, displayOrder: action.displayOrder });
        updateAction({ ...action, displayOrder: swap.displayOrder });
        updateAction({ ...swap, displayOrder: action.displayOrder });
    };

    const dropAction = (event: React.DragEvent<HTMLDivElement>, target: ComposerOptionActionNode) => {
        event.preventDefault();
        const dragged = findNodeById(event.dataTransfer.getData('text/plain'));
        if (!dragged || dragged.id === target.id) return;
        const siblings = [...actions].sort((left, right) => sortByDisplay(actions, left, right));
        const from = siblings.findIndex((action) => action.id === dragged.id);
        const to = siblings.findIndex((action) => action.id === target.id);
        const reordered = [...siblings];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        reordered.forEach((action, displayOrder) => {
            updateNode(action.id, { ...action, displayOrder });
            updateAction({ ...action, displayOrder });
        });
    };

    const toggleActionEnabled = (action: ComposerOptionActionNode) => {
        const currentAction =
            useComposerStore
                .getState()
                .attributes.find((attribute) => attribute.id === action.attributeID)
                ?.options.find((option) => option.id === action.optionID)
                ?.actions.find((candidate) => candidate.id === action.id) ?? action;
        const updatedAction = { ...currentAction, enabled: currentAction.enabled === false };
        updateAction(updatedAction);
        const validatedAction =
            useComposerStore
                .getState()
                .attributes.find((attribute) => attribute.id === updatedAction.attributeID)
                ?.options.find((option) => option.id === updatedAction.optionID)
                ?.actions.find((candidate) => candidate.id === updatedAction.id) ?? updatedAction;
        updateNode(validatedAction.id, validatedAction);
    };

    if (!selectedOption || multiple) {
        return (
            <ComposerPanel>
                <ComposerEmptyState icon={<OptionIcon />}>{T._('Select one option to see what it does.', 'Admin')}</ComposerEmptyState>
            </ComposerPanel>
        );
    }

    const nodes = [...filteredData].sort((left, right) => sortByDisplay(filteredData, left, right));
    const activeTab = optionFace === 'actions' ? 0 : optionFace === 'settings' ? 1 : optionFace === 'links' ? 2 : optionFace === 'prices' ? 3 : 4;
    const hasPrice =
        (selectedAttribute.pricings?.some((pricing) => pricing.selectedOptionGuid === selectedOption.id) ?? false) ||
        Boolean(parseComposerPlatformVariantCode(selectedOption.code));
    const priceSupported = [ComposerEcommercePlatform.WooCommerce, ComposerEcommercePlatform.Shopify, ComposerEcommercePlatform.BigCommerce].includes(
        ecommerceSettings?.ecommercePlatform ?? ComposerEcommercePlatform.None
    );
    const updateSelectedOption = (patch: Partial<ComposerAttributeOptionNode>) => {
        const updatedOption = { ...selectedOption, ...patch };
        updateOption(updatedOption);
        if (
            patch.name !== undefined ||
            patch.code !== undefined ||
            patch.links !== undefined ||
            patch.linkOperation !== undefined ||
            Object.prototype.hasOwnProperty.call(patch, 'platformAttribute')
        ) {
            void validateOptions({
                ...selectedAttribute,
                options: selectedAttribute.options.map((option) => (option.id === updatedOption.id ? updatedOption : option))
            });
        }
    };
    const updateSelectedOptionImage = async (uploadedImageUrl: string, tempImageFileKey?: string) => {
        const image = createComposerOptionImageReference(uploadedImageUrl, tempImageFileKey);
        updateOption(applyComposerOptionImage(selectedOption, image));
        return image?.imageUrl ?? '';
    };
    // A local option priced on its own is exempt from the code requirement, so its empty field must not read as broken.
    const hasCustomPricing = (selectedAttribute.pricings || []).some((pricing) => pricing.selectedOptionGuid === selectedOption.id);
    const isStoreConnected = Boolean(selectedAttribute.platformAttribute?.id);
    const isStoreOption = Boolean(selectedOption.platformAttribute?.id) || isComposerStoreCode(selectedOption.code);
    const storeOptionValues = isStoreConnected
        ? (merchantProductOptions.find((attribute) => attribute.optionID === selectedAttribute.platformAttribute?.id)?.values ?? [])
        : [];
    const storeOptions = [
        { value: '', label: T._('Select a store option', 'Admin') },
        ...storeOptionValues.map((option) => ({ value: option.valueID, label: option.label || option.valueID }))
    ];
    const optionNameHasError = hasComposerValidationError(
        selectedOption,
        ComposerValidationErrorCode.OPTION_NAME_REQUIRED,
        ComposerValidationErrorCode.OPTION_NAME_NOT_UNIQUE
    );
    const optionCodeHasError = hasComposerValidationError(
        selectedOption,
        ComposerValidationErrorCode.OPTION_CODE_REQUIRED,
        ComposerValidationErrorCode.OPTION_CODE_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );
    const optionStoreHasError = hasComposerValidationError(
        selectedOption,
        ComposerValidationErrorCode.OPTION_NOT_FROM_PLATFORM,
        ComposerValidationErrorCode.OPTION_FROM_PLATFORM_WITH_LOCAL_ATTRIBUTE,
        ComposerValidationErrorCode.STORE_OPTION_NOT_FOUND,
        ComposerValidationErrorCode.STORE_OPTION_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );

    return (
        <ComposerPanel aria-label={T._('Option details', 'Admin')}>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='option'>{selectedOption.img ? <img src={selectedOption.img} alt='' /> : <OptionIcon />}</ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={T._('Option name', 'Admin')}
                        placeholder={T._('Enter option name', 'Admin')}
                        isError={optionNameHasError}
                        value={selectedOption.name}
                        onChange={(event) => updateSelectedOption({ name: event.target.value })}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind='option'>{T._('Option', 'Admin')}</ComposerKindBadge>
                        {isStoreOption ? (
                            <ComposerKindBadge $kind='attribute'>{T._('Store attribute', 'Admin')}</ComposerKindBadge>
                        ) : (
                            <span>{selectedOption.code}</span>
                        )}
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
            </ComposerSubjectHeader>
            <ComposerTabs
                selectedIndex={activeTab}
                onSelect={(index) =>
                    setOptionFace(index === 0 ? 'actions' : index === 1 ? 'settings' : index === 2 ? 'links' : index === 3 ? 'prices' : 'properties')
                }
            >
                <TabList hasOverflow>
                    <Tab>
                        {T._('Actions', 'Admin')} <ComposerTabCount>{actions.length}</ComposerTabCount>
                    </Tab>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                    <Tab>
                        {T._('Links', 'Admin')} <ComposerTabCount>{selectedOption.links.length}</ComposerTabCount>
                    </Tab>
                    <Tab>
                        {T._('Prices', 'Admin')} <ComposerTabCount>{hasPrice ? 1 : 0}</ComposerTabCount>
                    </Tab>
                    <Tab>
                        {T._('Properties', 'Admin')} <ComposerTabCount>{selectedOption.extensionFields.length}</ComposerTabCount>
                    </Tab>
                </TabList>
                <TabPanel>
                    <ComposerToolbar>
                        <ComposerAddButton onClick={addAction}>
                            <PlusIcon />
                            {T._('Add', 'Admin')}
                        </ComposerAddButton>
                        <ComposerToolbarSpacer />
                        <ZTooltip title={T._('Run all actions', 'Admin')}>
                            <ComposerIconButton
                                aria-label={T._('Run all actions', 'Admin')}
                                disabled={actions.length === 0}
                                onClick={() => nodes.forEach(playAction)}
                            >
                                <PlayIcon />
                            </ComposerIconButton>
                        </ZTooltip>
                        {selectedIds.length > 0 && (
                            <ZTooltip title={T._('Remove selected actions', 'Admin')}>
                                <ComposerIconButton
                                    $danger
                                    aria-label={T._('Remove selected actions', 'Admin')}
                                    onClick={() => removeActions(selectedIds.map((id) => findNodeById(id)).filter(Boolean) as ComposerOptionActionNode[])}
                                >
                                    <DeleteIcon />
                                </ComposerIconButton>
                            </ZTooltip>
                        )}
                    </ComposerToolbar>
                    {nodes.length > 0 && (
                        <ActionTableHeader role='row'>
                            <span>{T._('What it does', 'Admin')}</span>
                            <span>{T._('Objects', 'Admin')}</span>
                            <span>{T._('Value', 'Admin')}</span>
                            <span aria-label={T._('Commands', 'Admin')} />
                        </ActionTableHeader>
                    )}
                    <ComposerScrollBody>
                        {nodes.length === 0 ? (
                            <ComposerEmptyState icon={<PlusIcon />}>
                                {T._('No actions yet. Add the first one to make this option change the 3D scene.', 'Admin')}
                            </ComposerEmptyState>
                        ) : (
                            <ComposerList role='table' aria-multiselectable='true'>
                                {nodes.map((action) => (
                                    <ComposerActionTreeElement
                                        key={action.id}
                                        action={action}
                                        selected={selectedIds.includes(action.id)}
                                        findMesh={(id) => meshes.find((mesh) => mesh.id === id || mesh.name === id)}
                                        findMaterial={(id) => materials.find((material) => material.id === id || material.name === id)}
                                        findCamera={(id) => cameras.find((camera) => camera.id === id || camera.name === id)}
                                        findPrintingMethod={(id) => {
                                            const method = printTypes.find((candidate) => String(candidate.printTypeID) === id);
                                            return method ? { name: method.name || id } : undefined;
                                        }}
                                        onDoubleClick={(_, selectedAction) => {
                                            setSelectedIds([selectedAction.id]);
                                            selectActions([selectedAction]);
                                            setOptionFace('actions');
                                        }}
                                        onDragOver={(event) => {
                                            event.preventDefault();
                                            event.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDragStart={(event, draggedAction) => {
                                            event.dataTransfer.setData('text/plain', draggedAction.id);
                                            event.dataTransfer.effectAllowed = 'move';
                                            setSelectedIds([draggedAction.id]);
                                            selectActions([draggedAction]);
                                        }}
                                        onDrop={(event) => dropAction(event, action)}
                                        onMoveDown={() => moveAction(action, 'down')}
                                        onMoveUp={() => moveAction(action, 'up')}
                                        onPlay={playAction}
                                        onRemove={(selectedAction) => removeActions([selectedAction])}
                                        onSelect={selectAction}
                                        onVisibilityClick={toggleActionEnabled}
                                        editor={
                                            selectedIds.length === 1 && selectedIds[0] === action.id ? (
                                                <ComposerSaveActionDialog
                                                    key={action.id}
                                                    embedded
                                                    edit={!action.isNew}
                                                    passedAction={action}
                                                    attributeID={selectedAttribute.id}
                                                    optionID={selectedOption.id}
                                                    materials={materials}
                                                    meshes={meshes}
                                                    cameras={cameras}
                                                    onCancel={() => cancelInlineAction(action)}
                                                    onConfirm={(savedAction) => saveInlineAction(action, savedAction)}
                                                />
                                            ) : undefined
                                        }
                                    />
                                ))}
                            </ComposerList>
                        )}
                    </ComposerScrollBody>
                </TabPanel>
                <TabPanel>
                    <ComposerSettingsBody>
                        <ComposerFormStack>
                            <ComposerFormGroup>
                                <ComposerFormSectionTitle>{T._('General', 'Admin')}</ComposerFormSectionTitle>
                                <ComposerFormSection>
                                    <FormControl label={T._('Code', 'Admin')}>
                                        {isStoreConnected ? (
                                            <ComposerDetailValue>
                                                {selectedOption.platformAttribute
                                                    ? selectedOption.platformAttribute.label || selectedOption.platformAttribute.id
                                                    : T._('Choose one of the connected store options below.', 'Admin')}
                                            </ComposerDetailValue>
                                        ) : (
                                            <Input
                                                placeholder={T._('Enter option code', 'Admin')}
                                                isError={optionCodeHasError && !hasCustomPricing}
                                                value={selectedOption.code}
                                                onChange={(event) => updateSelectedOption({ code: event.target.value })}
                                            />
                                        )}
                                    </FormControl>
                                    <FormControl label={T._('Description', 'Admin')}>
                                        <TextArea
                                            rows={4}
                                            placeholder={T._('Enter option description', 'Admin')}
                                            value={selectedOption.description}
                                            onChange={(event) => updateSelectedOption({ description: event.target.value })}
                                        />
                                    </FormControl>
                                    {isStoreConnected && (
                                        <FormControl label={T._('Store option', 'Admin')}>
                                            <AdvancedSelect
                                                isError={optionStoreHasError}
                                                options={storeOptions}
                                                value={storeOptions.find((option) => option.value === selectedOption.platformAttribute?.id) ?? storeOptions[0]}
                                                onChange={(selection) => {
                                                    const platformAttribute = selection?.value ? { id: selection.value, label: selection.label } : undefined;
                                                    updateSelectedOption({
                                                        platformAttribute,
                                                        code: platformAttribute
                                                            ? JSON.stringify({ ...platformAttribute, selected: true })
                                                            : createComposerLocalCode(selectedOption.name, 'option')
                                                    });
                                                }}
                                                isDisabled={storeOptionValues.length === 0}
                                                menuPosition='fixed'
                                            />
                                            {storeOptionValues.length === 0 && (
                                                <ComposerUnsupportedNote>
                                                    {T._('Store options are not available. Create them in the store before connecting this option.', 'Admin')}
                                                </ComposerUnsupportedNote>
                                            )}
                                        </FormControl>
                                    )}
                                </ComposerFormSection>
                            </ComposerFormGroup>
                            <ComposerFormGroup>
                                <ComposerFormSectionTitle>{T._('Preview image', 'Admin')}</ComposerFormSectionTitle>
                                <ComposerFormSection>
                                    <ComposerUploadComponent passedImgUrl={selectedOption.img} onImagePick={updateSelectedOptionImage} />
                                    <Composer3DPreviewCapture
                                        materials={materials}
                                        onImagePick={updateSelectedOptionImage}
                                        successMessage={T._('The 3D preview was assigned to this option.', 'Admin')}
                                    />
                                </ComposerFormSection>
                            </ComposerFormGroup>
                        </ComposerFormStack>
                    </ComposerSettingsBody>
                </TabPanel>
                <TabPanel>
                    <TabBody>
                        <ComposerLinksEditor
                            attributes={attributes}
                            links={selectedOption.links}
                            logicalOperator={selectedOption.linkOperation}
                            selfAttributeID={selectedAttribute.id}
                            subjectLabel={T._('This option', 'Admin')}
                            validationErrors={selectedOption.brokenValues}
                            onChange={(links, linkOperation) => updateSelectedOption({ links, linkOperation })}
                        />
                    </TabBody>
                </TabPanel>
                <TabPanel>
                    <TabBody>
                        {priceSupported ? (
                            <PriceContent
                                selectedAttribute={selectedAttribute}
                                selectedOption={selectedOption}
                                ecommercePlatform={ecommerceSettings?.ecommercePlatform ?? ComposerEcommercePlatform.None}
                                onUpdateAttribute={updateAttribute}
                                onUpdateOption={updateSelectedOption}
                            />
                        ) : (
                            <ComposerUnsupportedNote>
                                {T._('Option pricing is available for WooCommerce, Shopify, and BigCommerce.', 'Admin')}
                            </ComposerUnsupportedNote>
                        )}
                    </TabBody>
                </TabPanel>
                <TabPanel>
                    <TabBody>
                        <PropertiesContent selectedOption={selectedOption} onUpdate={updateOption} />
                    </TabBody>
                </TabPanel>
            </ComposerTabs>
        </ComposerPanel>
    );
};

export default ComposerActions;
